// Flat cartoon's form (spec G14-flat-cartoon, PR d). The playbook is its spec and live has no
// widget, so this form has no live capture (Flat cartoon-6). It is built on the kit of Pixar
// Talking Ad's form, from the playbook's Fill in (L3625-3637), in two approvals. The script stage
// picks the product and takes the brand as said, the web address and the caller's problem, then
// writes the six lines and the cast (the server's pack reader and director, or the member) and
// approves them. The cast stage shows the eight images (`FlatCartoonCast.tsx`). "Generate video"
// starts the run (`flat_cartoon.py`). The draft is the server's (`flat-cartoon-draft.ts`).
import { Group, Loader, Stack, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { Captions, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { FlatCartoonInputsResolution, FlatCartoonScript, Job } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  flatCartoonInputsBrandMax,
  flatCartoonInputsProblemMax,
  flatCartoonInputsUrlMax,
  type FlatCartoonInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { FlatCartoonCast } from "./FlatCartoonCast";
import { InputChip, ScriptSection } from "./PodcastScript";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import {
  blankScript,
  scriptProblems,
  useFlatCartoonDraft,
  videoBody,
  type DraftWork,
  type VideoSettings,
} from "./flat-cartoon-draft";
import classes from "./podcast.module.css";

const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product the host prescribes and the hero shot shows. Pick one of your products and its packshot, or " +
    "upload a packshot: every product image is drawn from it.",
  name: "The product's name. Leave empty and it is read off the packshot.",
  text: "A description or the product page. The script states only what it says.",
};
const SCRIPT_TOOLTIP =
  "Six lines: the host opens, the caller's problem, the narrator over the metaphor, the host prescribes the " +
  "product, three benefits, the narrator's close with the tagline. About 60 to 65 words in all.";
const CAST_TOOLTIP =
  "The invented cast and the product as every image draws them. Each lock needs its hex colors (#RRGGBB).";
const SUBTITLES_TOOLTIP = "White subtitles on a dark box at the bottom, in the script's spelling.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about two thirds of 1080p.";
const FORMAT_TOOLTIP = "Every image and clip is vertical 9:16.";
const PICK_PRODUCT = "Pick a product and its packshot";
const FILL_IN_BRAND = "Fill in the brand as it is said";
const GENERATE_SCRIPT = "Generate the script";
const FIX_CAST = "Fix the fields marked under The cast and the product";
const FIX_LINES = "Fix the script's lines";
const STILLS_PENDING = "Wait for the eight images";
const UNREADABLE_PACK = "The pack was hard to read: check the product's name and text.";
const HANDOFF_NOTICE = "Your Flat cartoon ad is finishing in My Generations: you can close this panel now.";
const RESOLUTIONS: readonly FlatCartoonInputsResolution[] = ["1080p", "720p"];
const BEATS = ["Opening", "Problem", "Metaphor", "Prescription", "Benefits", "Close"] as const;
const SPEAKER_NAMES = { HOST: "Host", CALLER: "Caller", NARRATOR: "Narrator" } as const;
/** About 3.5 words a second in a clip of at most 10 s. */
const MAX_LINE_WORDS = 31;
const CHIPS = [
  {
    field: "brand",
    label: "Brand",
    placeholder: "Everyday Dose",
    tooltip: 'The brand as it is said: the host says "switch to" it.',
    maxLength: flatCartoonInputsBrandMax,
  },
  {
    field: "url",
    label: "Web address",
    placeholder: "everydaydose.com (optional)",
    tooltip: "Shown under the tagline at the end. Leave empty and only the tagline shows.",
    maxLength: flatCartoonInputsUrlMax,
  },
  {
    field: "problem",
    label: "Problem",
    placeholder: "The afternoon crash (optional)",
    tooltip: "What the caller struggles with. Leave empty and the director takes it from the product text.",
    maxLength: flatCartoonInputsProblemMax,
  },
] as const;
/** The cast's and the product's words the member may edit before approving. */
const CAST_FIELDS: readonly { field: keyof FlatCartoonScript; label: string }[] = [
  { field: "caller_name", label: "Caller's name" },
  { field: "host_lock", label: "Host's lock" },
  { field: "caller_lock", label: "Caller's lock" },
  { field: "runner_lock", label: "Metaphor's figure" },
  { field: "problem_metaphor", label: "Metaphor" },
  { field: "metaphor_action", label: "Metaphor's action" },
  { field: "car_props", label: "Props in the car" },
  { field: "product_description", label: "Product's description" },
  { field: "accent_colors", label: "Product's colors" },
  { field: "product_short", label: "Product's short phrase" },
  { field: "pack_word", label: "Pack's word" },
  { field: "tagline", label: "Tagline" },
  { field: "benefit_number", label: "Benefits' number" },
  { field: "host_recap", label: "Host's recap" },
  { field: "host_features", label: "Host's features" },
  { field: "caller_recap", label: "Caller's recap" },
  { field: "caller_features", label: "Caller's features" },
  { field: "runner_recap", label: "Figure's recap" },
  { field: "runner_features", label: "Figure's features" },
];

function words(text: string): number {
  return text.split(/\s+/u).filter((each) => each !== "").length;
}

function progressOf(work: DraftWork, drawn: number, total: number): string | null {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the script";
  return work.drawing ? `Generating images · ${drawn}/${total}` : null;
}

/** The cast's and the product's words, folded under the script until opened. */
function CastFields(props: {
  script: FlatCartoonScript;
  problems: Partial<Record<keyof FlatCartoonScript, string>>;
  locked: boolean;
  onChange: (script: FlatCartoonScript) => void;
}) {
  const { script, problems, locked, onChange } = props;
  const refused = CAST_FIELDS.some(({ field }) => problems[field] !== undefined);
  return (
    <details className={classes.chips} open={refused || undefined}>
      <summary>The cast and the product</summary>
      <Stack gap={8}>
        <Text size="xs">{CAST_TOOLTIP}</Text>
        {CAST_FIELDS.map(({ field, label }) => (
          <Textarea
            key={field}
            label={label}
            autosize
            minRows={1}
            value={String(script[field])}
            error={problems[field]}
            disabled={locked}
            onChange={(event) => onChange({ ...script, [field]: event.currentTarget.value })}
          />
        ))}
      </Stack>
    </details>
  );
}

/**
 * The form: the stage, and in the footer the subtitles, the resolution, the 9:16 format and the round
 * start, "Approve script" then "Generate video", whose tooltip says what it waits for. The subtitles
 * and the resolution stay editable after Approve script.
 */
export function FlatCartoonForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useFlatCartoonDraft(teamId, brandId);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const [productAsked, setProductAsked] = useState(false);
  const [started, setStarted] = useState(false);
  const { form, draft } = flow;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? { subtitles: form.inputs.subtitles, resolution: form.inputs.resolution });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(true);
      onStarted(job);
    },
  });
  if (!approved && settings !== null) setSettings(null);

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Flat cartoon</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const problems = scriptProblems(form);
  const setInputs = (change: Partial<FlatCartoonInputsOutput>) => flow.editInputs(change);
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const locked = !flow.editable;
  const work = flow.work;
  const writing = work.reading || work.writing;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(work, drawn, draft.stills.length);

  let submit;
  if (approved) {
    const ready = draft.stills.length > 0 && drawn === draft.stills.length;
    submit = (
      <Submit
        label="Generate video"
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || videoStart.start.isPending}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(inputs.brand.trim() === "" ? [FILL_IN_BRAND] : []),
      ...(script === null ? [GENERATE_SCRIPT] : []),
      ...(CAST_FIELDS.some(({ field }) => problems[field] !== undefined) ? [FIX_CAST] : []),
      ...(problems.lines === undefined ? [] : [FIX_LINES]),
    ];
    submit = (
      <Submit
        label="Approve script"
        reasons={reasons}
        disabled={reasons.length > 0 || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Tooltip label={SUBTITLES_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={shownSettings.subtitles || undefined}
            aria-pressed={shownSettings.subtitles}
            disabled={flow.busy}
            onClick={() => changeSettings({ subtitles: !shownSettings.subtitles })}
          >
            <Captions size={16} strokeWidth={1.5} aria-hidden />
            {shownSettings.subtitles ? "Subtitles on" : "Subtitles off"}
          </UnstyledButton>
        </Tooltip>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {started && approved ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <FlatCartoonCast
          stills={draft.stills}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={flow.regenerate}
          onStartOver={() => {
            setStarted(false);
            flow.startOver();
          }}
        />
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [...(draft.pack_warning ? [UNREADABLE_PACK] : []), ...draft.script_problems];
  const total = script === null ? 0 : script.lines.reduce((sum, line) => sum + words(line.text), 0);
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => setInputs({ [chip.field]: value })}
            />
          ))}
        </div>
        <ScriptSection
          lines={
            script === null
              ? null
              : script.lines.map((line, at) => ({
                  id: at + 1,
                  speaker: SPEAKER_NAMES[line.speaker],
                  beat: BEATS[at] ?? "",
                  text: line.text,
                }))
          }
          meta={script === null ? null : `${script.lines.length} lines · ${total} words (60–65)`}
          tooltip={SCRIPT_TOOLTIP}
          primary={SPEAKER_NAMES.HOST}
          maxWords={() => MAX_LINE_WORDS}
          hint={{ wand: "to write the six lines and the cast from the product, or", pencil: "to write them yourself." }}
          writeLabel="Write the six lines yourself"
          writing={writing}
          locked={locked}
          issue={null}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => flow.edit({ script: blankScript() })}
          onLine={(index, text) => {
            if (script === null) return;
            const lines = script.lines.map((line, at) => (at === index ? { ...line, text } : line));
            flow.edit({ script: { ...script, lines } });
          }}
        />
        {script === null ? null : (
          <CastFields
            script={script}
            problems={problems}
            locked={locked}
            onChange={(next) => flow.edit({ script: next })}
          />
        )}
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
